import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import PersonAvatar from '../../components/common/PersonAvatar';
import { apiErrorText, apiRequest } from '../../utils';
import { TEAM_COLUMNS, buildCsv, downloadCsv, parseContactsCsv } from '../../csv';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyTournament.css';

/* The field that competes — the teams of one company event, and the ways of filling them.

   ★★ Making teams and filling them are TWO steps, and that is the design. "Team 1..12" exists
   before anybody is in it; every filling method fills teams that already exist. Collapse the
   two and each method grows its own idea of how a team gets named.

   ★★ **Three screens, split by the LIST each one works from.** It used to be one screen with
   everything stacked on it, and that was right while the only long thing on it was the teams.
   It no longer is: twelve teams with their rosters, everybody who is coming and is in no team,
   and now the whole company's list of colleagues are three long lists, and a screen that holds
   all three is a screen you scroll past the thing you came for.

       the teams   — make them, name them, see who is in them
       who's coming — the people who said yes and sit in no team: seat them, or shuffle them
       invite       — a colleague who has not said anything yet, picked off the company's list

   ★ This is NOT "one screen per way of filling a team", which is what the earlier comment here
   argued against and still argues against — an organiser uses two or three ways on the same
   afternoon. Shuffling, seating by hand and the spreadsheet round trip all work from the SAME
   list (who is coming), so they stay together on one screen. The cut is the list, not the method.

   ★ The step lives in the URL (?step=coming), so a reload, a back press or a pasted link lands
   where the organiser was. Same device the People screen uses for its guest list (?tab=invites).

   ★ Bulk paths do not mail by default. Seating one person by hand tells them (the mail and the
   bell already exist); shuffling 120 people would send 120 of those, so that tick starts off.

   ★ Uploading is not inviting. The CSV seats addresses this event already knows and REPORTS the
   rest — it does not create people out of a spreadsheet.

   ★★ **Inviting is PICKED, never typed.** "Their email address" was the only way in, which on a
   roster of 56 colleagues means retyping from memory something the platform already holds — and
   it is why "Send it" sat greyed out with nothing on screen saying what was missing. It is a
   search box over the company's own people now, and what a disabled button is waiting for is
   written underneath it.

   ★★ **The JOKER is on the team row, because nowhere else says it.** A team puts its joker on one
   game itself, from the battle screen, and from then on it is visible only as a star inside the
   one heat it lands in — so "who has not chosen yet, and who chose what" was a question only the
   back office could answer, on the morning it was already too late to ask. It is a picker and not
   just a label: the organiser is the one who has to be able to put it right. Moving it after that
   game has been played is an OVERRIDE, and the screen says so rather than doing it quietly.

   ★ **And the team's own edit screen is a click away.** The same one Hercules Trophy and Pink
   Ladies Games teams have had for years (`/edit-team`, reached with `otherTeamId`) — photo,
   slogan, song, bio. Not rebuilt here: an organiser who has to step in needs the screen the
   captain sees, not a second one that drifts from it. */

const STEPS = ['teams', 'coming', 'invite'];

const CompanyEventTeams = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [draft, setDraft] = useState(null);          // the "make N teams" form
  const [renaming, setRenaming] = useState(null);    // {team_id, name}
  const [target, setTarget] = useState('');          // which team the picker seats onto
  const [notify, setNotify] = useState(false);
  const [invite, setInvite] = useState({ team_id: '', partner_id: '', link: '' });
  const [search, setSearch] = useState('');          // narrowing the colleague list
  const [report, setReport] = useState(null);        // what an import or a shuffle did
  const [working, setWorking] = useState(0);         // rows an upload is chewing through NOW
  const file = useRef(null);

  /* ★ A HOST reads this screen and does not write it (`can_manage` is the company's), so the two
     filling steps are not theirs — and a pasted ?step=invite must land them on the team list
     rather than on a page with nothing but the summary line on it. */
  const asked = params.get('step');
  const step = (STEPS.includes(asked) && (data?.can_manage || asked === 'teams'))
    ? asked : 'teams';
  // `replace`, so walking teams → coming → invite does not bury the tournament screen under
  // three history entries the Back arrow then has to be pressed through.
  const goTo = (next) => {
    setParams(next === 'teams' ? {} : { step: next }, { replace: true });
    setReport(null);
    setNotice(null);
  };

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/teams`)
      .then(setData)
      .catch((e) => setNotice(apiErrorText(t, e)));
  }, [eventId, t]);

  useEffect(() => { load(); }, [load]);

  const call = (method, path, body) => {
    setBusy(true);
    setNotice(null);
    return apiRequest(method, `/company/event/${eventId}${path}`, body)
      .then((res) => { setData((d) => ({ ...d, ...res })); return res; })
      .catch((e) => { setNotice(apiErrorText(t, e)); throw e; })
      .finally(() => setBusy(false));
  };

  const makeTeams = () => {
    call('POST', '/teams', {
      count: draft.count, start_number: draft.start_number, prefix: draft.prefix,
    }).then(() => setDraft(null)).catch(() => {});
  };

  const seat = (partnerId) => {
    if (!target) { setNotice(t('teamsPickATeamFirst')); return; }
    call('POST', `/team/${target}/players`, { partner_id: partnerId, notify: true })
      .catch(() => {});
  };

  const shuffle = () => {
    call('POST', '/teams/shuffle', { notify })
      .then((res) => setReport({ kind: 'shuffle', ...res }))
      .catch(() => {});
  };

  /* Move a team's joker, or take it off (''). The server decides whether that was an ordinary
     change or an override of a game already played — only it knows what has been scored — and
     says how many results it had to restate, which is the one thing worth saying out loud. */
  const setJoker = (team, jokerId) => {
    call('PUT', `/team/${team.team_id}`, { joker_id: jokerId })
      .then((res) => {
        if (res.joker_overridden) {
          setNotice(t('teamsJokerOverridden', { name: team.name, n: res.joker_restated || 0 }));
        }
      })
      .catch(() => {});
  };

  /* The team's own edit screen — the one its captain uses. `otherTeamId` is how every caller on
     this platform says "act on that team instead"; the screen reads it back off sessionStorage. */
  const openTeamEdit = (team) => {
    sessionStorage.setItem('otherTeamId', team.team_id);
    navigate('/edit-team');
  };

  // ★ `partner_id`, not an address. The route reads the name and the email off the Partner row —
  // and on an invite-only event it also puts them on the guest list, which a typed address could
  // never do because an address is not a person yet.
  const sendInvite = () => {
    call('POST', `/team/${invite.team_id}/invite`, { partner_id: invite.partner_id })
      .then((res) => {
        setInvite((v) => ({ ...v, partner_id: '', link: res.invite_link }));
        setNotice(t('teamsInviteSent', { email: res.email || '' }));
      })
      .catch(() => {});
  };

  // Same-origin, and the route sets Content-Disposition, so the browser downloads and stays on
  // the page. The JWT rides on the cookie exactly as it does for every other call.
  const exportCsv = () => {
    const a = document.createElement('a');
    a.href = `/api/company/event/${eventId}/teams.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  /* The four columns an upload reads, with THIS event's own team names already in the last one.
     Built in the browser, because it is four lines of text and the server route next door is for
     the real list, not for an example of one. */
  const downloadTemplate = () => {
    const names = (data?.teams || []).map((x) => x.name).filter(Boolean);
    const shown = (names.length ? names : ['Team 1']).slice(0, 3);
    downloadCsv('team-sheet-template.csv', buildCsv([
      TEAM_COLUMNS,
      ...shown.map((name, i) => [`First name ${i + 1}`, `Last name ${i + 1}`,
                                 `colleague${i + 1}@company.com`, name]),
    ]));
  };

  const upload = (event) => {
    const chosen = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!chosen) return;
    const reader = new FileReader();
    reader.onload = () => {
      // The BROWSER parses the file; the API takes plain JSON rows, the way every upload on
      // this platform does.
      const rows = parseContactsCsv(reader.result).filter((r) => r.team);
      if (!rows.length) { setNotice(t('teamsCsvNothing')); return; }
      /* ★★ Say that it is running, and say how long. Seating one person is a mail, a bell and a
         first-participation check — around two seconds each — so a file of a hundred colleagues
         is minutes of a screen that looks exactly like a screen that has died. The organiser
         then does the one thing that actually loses the answer: they reload, or upload again.
         The count is in the sentence because "a few minutes" for 8 rows would be a lie and for
         800 would be a comfort. */
      setReport(null);
      setWorking(rows.length);
      call('POST', '/teams/import', { rows, notify })
        .then((res) => setReport({ kind: 'import', ...res }))
        .catch(() => {})
        .finally(() => setWorking(0));
    };
    reader.readAsText(chosen);
  };

  const roster = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ((data && data.roster) || []).filter((p) => !q
      || (p.name || '').toLowerCase().includes(q)
      || (p.email || '').toLowerCase().includes(q));
  }, [data, search]);

  if (!data) return <Base activeNav="home"><Card /></Base>;

  const { teams, unseated, can_manage: canManage, can_delete: canDelete, event } = data;
  // The games this day pays a joker on. Empty is a real answer — an event can pay none — and the
  // row then says that instead of offering a picker with nothing in it.
  const jokerGames = data.joker_games || [];
  const locked = !canManage || busy;

  /* ★ How many teams to make is already answered on the competition screen — "Teams" in the
     dimensions is the plan for the day. Offering 4 out of the air made an organiser who had just
     typed 20 type it a second time, in another place, where the two could disagree. Defaults to
     what is still MISSING, so pressing it after making a few does the rest rather than doubling
     up. Falls back to 4 only when the plan says nothing. */
  const planned = event?.amount_of_teams || 0;
  const stillToMake = planned ? Math.max(1, planned - teams.length) : 4;

  const cap = event?.players_per_team || 0;
  const full = (team) => !!cap && team.players.length >= cap;
  const chosen = (data.roster || []).find((p) => p.id === invite.partner_id) || null;
  // Only ever true on an invite-only event — `invited_ids` is an empty list otherwise, and the
  // note it drives would then be a warning about a door that is open.
  const needsGuestRow = !!(event?.is_invite_only && chosen
    && !(data.invited_ids || []).includes(chosen.id));

  /* ★★ **Why the pool is empty, not just that it is.** Three completely different things produce
     "nobody is coming": nobody has answered yet, nobody MAY answer because registration is off,
     or the deadline has passed. Only the first is a matter of waiting; the other two are a switch
     somebody has to go and flip, so the screen says which it is and links straight at it.

     Registration lives on the host's settings screen and the deadline on the editor, so the link
     goes where the thing actually is — sending somebody to the wrong screen to change a setting
     they can see named on this one is worse than not linking at all. */
  const blocker = (() => {
    if (event?.registration_enabled === false) {
      return { text: t('teamsRegistrationOff'), to: `/host-event/${eventId}/settings`,
               label: t('teamsOpenEventSettings') };
    }
    if (event?.registration_closed) {
      return { text: t('teamsRegistrationClosed'), to: `/company-event/${eventId}/edit`,
               label: t('teamsOpenEventEditor') };
    }
    return null;
  })();

  // What a disabled "Send it" is waiting for, said out loud. A greyed-out button with nothing
  // beside it is the whole reason this screen was rewritten.
  const inviteMissing = (!invite.team_id && t('teamsInviteNeedsTeam'))
    || (!invite.partner_id && t('teamsInviteNeedsPerson')) || null;

  const teamChip = (team) => (
    <button
      key={team.team_id}
      type="button"
      className={`ce-tab ${target === team.team_id ? 'is-on' : ''}`}
      disabled={locked || full(team)}
      title={full(team) ? t('teamsTeamFull', { n: cap }) : team.name}
      onClick={() => setTarget(target === team.team_id ? '' : team.team_id)}
    >
      {team.name}
      <span className="ce-tab__n">{cap ? `${team.players.length}/${cap}` : team.players.length}</span>
    </button>
  );

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/company-event/${eventId}/tournament`} />
        <Title my="1" className="mt-3">
          <Icon name="users" theme="primary" className="me-2" />
          {t('tournamentTheField')}
        </Title>
        <p className="ct-sum">
          {t('teamsSummary', {
            teams: teams.length,
            seated: data.seated_count,
            waiting: unseated.length,
          })}
        </p>

        {/* ---- THE THREE SCREENS ----
            Always all three, and always in this order: teams exist before anybody can be put in
            one, and an organiser reads the row as the order of the afternoon. The two filling
            screens are offered even with no teams yet — they then say what is missing, which is
            a step, where a hidden tab is a dead end. */}
        {canManage && (
          <div className="ce-tabs">
            {STEPS.map((key) => (
              <button
                key={key}
                type="button"
                className={`ce-tab ${step === key ? 'is-on' : ''}`}
                onClick={() => goTo(key)}
              >
                {t(`teamsStep_${key}`)}
                {key === 'teams' && <span className="ce-tab__n">{teams.length}</span>}
                {key === 'coming' && <span className="ce-tab__n">{unseated.length}</span>}
              </button>
            ))}
          </div>
        )}
        {canManage && <p className="ct-pick__note">{t(`teamsStepNote_${step}`)}</p>}
        {notice && <p className="ce-note ce-note--warn">{notice}</p>}

        {/* ================= 1 — THE TEAMS ================= */}
        {step === 'teams' && (
          <>
            {teams.length === 0 && (
              <p className="ct-field__hint">{t('teamsNoTeamsYet')}</p>
            )}
            {teams.map((team) => (
              <div key={team.team_id} className="ct-team">
                <div className="ct-team__head">
                  {renaming?.team_id === team.team_id ? (
                    <>
                      <input
                        className="ct-team__rename"
                        value={renaming.name}
                        maxLength={50}
                        onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                      />
                      <button type="button" className="ce-person__act" disabled={busy}
                              onClick={() => call('PUT', `/team/${team.team_id}`,
                                { name: renaming.name }).then(() => setRenaming(null))
                                .catch(() => {})}>
                        <Icon name="check" />
                      </button>
                      <button type="button" className="ce-person__act"
                              onClick={() => setRenaming(null)}>
                        <Icon name="cross-small" />
                      </button>
                    </>
                  ) : (
                    <>
                      {/* A name, not a button. Choosing WHICH team to seat onto belongs on the
                          screen where the seating happens — it was a toggle here, two screens
                          away from the list of people it governs. */}
                      <span className="ct-team__name">
                        {team.name}
                        <span className="ce-tab__n">
                          {cap ? `${team.players.length}/${cap}` : team.players.length}
                        </span>
                      </span>
                      {!team.has_captain && (
                        <span className="ce-pill ce-pill--draft">{t('teamsNoCaptain')}</span>
                      )}
                      {canManage && (
                        <span className="ce-person__acts">
                          <button type="button" className="ce-person__act"
                                  title={t('rename')}
                                  onClick={() => setRenaming({ team_id: team.team_id,
                                                               name: team.name })}>
                            <Icon name="pencil" />
                          </button>
                          {/* ★ The captain's own edit screen, on this team. Renaming is right
                              here because it is the one field an organiser changes constantly;
                              the photo, the song and the bio live where they always have. */}
                          <button type="button" className="ce-person__act"
                                  title={t('teamsOpenTeamEdit')}
                                  onClick={() => openTeamEdit(team)}>
                            <Icon name="settings" />
                          </button>
                          {/* ⚠️ Deleting a team takes its heats and scores with it, so it is
                              offered only while nothing has been generated. */}
                          <button type="button" className="ce-person__act"
                                  disabled={!canDelete || busy}
                                  title={canDelete ? t('delete') : t('tournamentFormatLocked')}
                                  onClick={() => call('DELETE', `/team/${team.team_id}`)
                                    .catch(() => {})}>
                            <Icon name="trash" />
                          </button>
                        </span>
                      )}
                    </>
                  )}
                </div>
                {/* ★★ WHERE THIS TEAM PUT ITS JOKER.
                    A label when there is nothing to choose from (no joker games on this day, or
                    a host who only reads this screen), a picker when there is. `team.joker` with
                    no `id` is the third state: a joker standing on a game that has since been
                    taken off the day — said in as many words, because a blank there would read as
                    "never chose one" and send somebody chasing a team that did. */}
                {(jokerGames.length > 0 || team.joker) && (
                  <div className="ct-team__joker">
                    <Icon name="star" className="me-1" />
                    {canManage && jokerGames.length > 0 ? (
                      <select
                        className="ct-team__joker-pick"
                        value={team.joker?.id || ''}
                        disabled={locked}
                        onChange={(e) => setJoker(team, e.target.value)}
                      >
                        <option value="">{t('teamsJokerNone')}</option>
                        {jokerGames.map((game) => (
                          <option key={game.id} value={game.id}>{game.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="ct-field__hint">
                        {team.joker ? (team.joker.name || t('teamsJokerGameGone'))
                          : t('teamsJokerNone')}
                      </span>
                    )}
                    {/* Only beside the PICKER: the label above already says it in words, and a
                        picker on its own would show "no joker yet" for a team that did choose. */}
                    {canManage && jokerGames.length > 0 && team.joker && !team.joker.id && (
                      <span className="ce-pill ce-pill--draft">{t('teamsJokerGameGone')}</span>
                    )}
                  </div>
                )}
                <div className="ct-team__people">
                  {team.players.length === 0 && (
                    <span className="ct-field__hint">{t('teamsNobodyYet')}</span>
                  )}
                  {team.players.map((person) => (
                    <span key={person.id} className="ct-chip">
                      {person.is_captain && <Icon name="user-crown" className="me-1" />}
                      {person.name}
                      {canManage && (
                        <>
                          {!person.is_captain && (
                            <button type="button" className="ct-chip__act" disabled={locked}
                                    title={t('teamsMakeCaptain')}
                                    onClick={() => call('PUT', `/team/${team.team_id}`,
                                      { captain_id: person.id }).catch(() => {})}>
                              <Icon name="user-crown" />
                            </button>
                          )}
                          <button type="button" className="ct-chip__act" disabled={locked}
                                  title={t('teamsTakeOff')}
                                  onClick={() => call('DELETE',
                                    `/team/${team.team_id}/player/${person.id}`).catch(() => {})}>
                            <Icon name="cross-small" />
                          </button>
                        </>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}

            {/* ---- MAKING THEM ----
                The step in front of every way of filling them. */}
            {canManage && (draft ? (
              <div className="bravos-form mt-2">
                <div className="lg-two">
                  <span>
                    <label>{t('leagueHowManyTeams')}</label>
                    <input type="number" min="1" max="100" value={draft.count}
                           onChange={(e) => setDraft({ ...draft, count: e.target.value })} />
                  </span>
                  <span>
                    <label>{t('leagueStartNumber')}</label>
                    <input type="number" min="1" value={draft.start_number}
                           onChange={(e) => setDraft({ ...draft,
                                                       start_number: e.target.value })} />
                  </span>
                </div>
                <label>{t('leagueTeamPrefix')}</label>
                <input value={draft.prefix} maxLength={40} placeholder={t('leagueTeamPrefixHint')}
                       onChange={(e) => setDraft({ ...draft, prefix: e.target.value })} />
                <p className="ce-check__hint">{t('leagueTeamNamesHint')}</p>
                <div className="ce-actions">
                  <button type="button" className="btn btn-primary" disabled={busy}
                          onClick={makeTeams}>{t('leagueCreateTeams')}</button>
                  <button type="button" className="bravos-member__makeadmin"
                          onClick={() => setDraft(null)}>{t('cancel')}</button>
                </div>
              </div>
            ) : (
              <button type="button" className="bravos-member__makeadmin mt-2"
                      onClick={() => setDraft({ count: stillToMake, prefix: '',
                                                start_number: teams.length + 1 })}>
                <Icon name="plus" className="me-1" />{t('leagueMakeTeamsButton')}
              </button>
            ))}

            {cap ? (
              <p className="ce-check__hint mt-3">{t('teamsCapHint', { n: cap })}</p>
            ) : null}
          </>
        )}

        {/* ================= 2 — WHO'S COMING =================
            One list, three ways of working from it: the two bulk ones sit above it as a band
            because they are three lines each, and the long list stays the bottom of the screen
            where it can be scrolled without pushing anything out of reach. */}
        {step === 'coming' && (
          <>
            {/* ⚠️⚠️ NOT gated on `canManage` any more, and that was a dead door: the tab itself is
                always drawn and carries a COUNT, so a host who is not the company admin read "4"
                on the tab and then found an entirely empty page under it. Nothing said why.
                Looking at who is coming is not the same act as seating them — the first is what a
                host runs the day on, the second changes who plays — so the LIST is for anyone who
                may open this screen and every ACTION below stays the admin's. */}
            {!canManage && <p className="ce-note">{t('teamsLookOnly')}</p>}
            {/* The answer to "why is nobody here", before the empty list rather than under it. */}
            {blocker && (
              <p className="ce-note ce-note--warn">
                {blocker.text}{' '}
                <Link to={blocker.to}>{blocker.label}</Link>
              </p>
            )}
            {teams.length === 0 ? (
              <p className="ce-note">
                {t('teamsMakeTeamsFirst')}{' '}
                <button type="button" className="ct-linkbtn" onClick={() => goTo('teams')}>
                  {t('teamsStep_teams')}
                </button>
              </p>
            ) : (
              <>
                <p className="ct-field__hint">
                  {t('teamsShuffleCount', { n: unseated.length, teams: teams.length })}
                </p>

                {/* ---- the two bulk ways ---- */}
                {canManage && (
                <>
                <label className="ce-check">
                  <input type="checkbox" checked={notify}
                         onChange={(e) => setNotify(e.target.checked)} />
                  <span>
                    {t('teamsTellThem')}
                    <span className="ce-check__hint">{t('teamsTellThemHint')}</span>
                  </span>
                </label>
                <div className="ce-actions">
                  <button type="button" className="btn btn-primary"
                          disabled={locked || unseated.length === 0} onClick={shuffle}>
                    <Icon name="refresh" className="me-1" />{t('teamsShuffleNow')}
                  </button>
                  <button type="button" className="bravos-member__makeadmin"
                          onClick={exportCsv}>
                    <Icon name="download" className="me-1" />{t('teamsCsvExport')}
                  </button>
                  <button type="button" className="bravos-member__makeadmin" disabled={locked}
                          onClick={() => file.current && file.current.click()}>
                    <Icon name="file-download" className="me-1" />{t('teamsCsvUpload')}
                  </button>
                </div>
                <p className="ce-check__hint">
                  {t('teamsCsvHint')}
                  {' '}
                  <button type="button" className="ct-linkbtn" onClick={downloadTemplate}>
                    <Icon name="download" className="me-1" />{t('teamsTemplate')}
                  </button>
                </p>
                <input ref={file} type="file" accept=".csv,text/csv"
                       style={{ display: 'none' }} onChange={upload} />
                </>
                )}

                {/* ★ In the same place the answer will appear, and above the list it changes. */}
                {working > 0 && (
                  <p className="ce-note ct-working">
                    <Icon name="clock" className="me-1" />
                    {t('teamsCsvWorking', { n: working })}
                  </p>
                )}
                {report && (
                  <div className="ct-report">
                    {/* Three different afternoons, and they are named apart: people newly put in
                        a team, people the sheet MOVED to another one, and people who were already
                        exactly where the sheet says. Without the last one a file that matches the
                        screen reads "0 people put in a team", which is what "nothing happened"
                        looks like. */}
                    <p className="ct-sum">
                      {[t('teamsSeatedN', { n: report.seated }),
                        report.moved ? t('teamsMovedN', { n: report.moved }) : null,
                        report.unchanged ? t('teamsUnchangedN', { n: report.unchanged }) : null,
                      ].filter(Boolean).join(' ')}
                    </p>
                    {report.unknown?.length > 0 && (
                      <p className="ce-note ce-note--warn">
                        {t('teamsCsvUnknown', { n: report.unknown.length,
                                                list: report.unknown.slice(0, 5).join(', ') })}
                      </p>
                    )}
                    {report.unknown_teams?.length > 0 && (
                      <p className="ce-note ce-note--warn">
                        {t('teamsCsvUnknownTeams', { list: report.unknown_teams.join(', ') })}
                      </p>
                    )}
                    {report.skipped?.length > 0 && (
                      <p className="ce-note ce-note--warn">
                        {t('teamsSkippedN', { n: report.skipped.length })}
                      </p>
                    )}
                    {report.failed?.length > 0 && (
                      <p className="ce-note ce-note--warn">
                        {t('teamsSkippedN', { n: report.failed.length })}
                      </p>
                    )}
                  </div>
                )}

                {/* ---- who is coming, and (for an admin) seating them by hand ---- */}
                <Title my="1" className="mt-3">
                  <Icon name="user-add" theme="primary" className="me-2" />
                  {canManage ? t('teamsSeatByHand') : t('teamsWhoIsComing')}
                </Title>
                {/* ★ The team chooser is HERE, beside the people it seats. A full team is
                    disabled rather than hidden: "Team 3 2/2" is the reason you cannot pick it. */}
                {canManage && (
                  <>
                    <div className="ce-tabs">{teams.map(teamChip)}</div>
                    <p className="ct-field__hint">
                      {target ? t('teamsSeatingOnto', {
                        team: teams.find((x) => x.team_id === target)?.name || '',
                      }) : t('teamsPickATeamFirst')}
                    </p>
                  </>
                )}
                <div className="ct-pool">
                  {unseated.length === 0 && (
                    <span className="ct-field__hint">{t('teamsEverybodySeated')}</span>
                  )}
                  {/* ★ A chip either seats somebody or it is just their name. Disabled rather than
                      turned into a different element, so the list reads the same to everybody and
                      only stops responding. */}
                  {unseated.map((person) => (
                    <button key={person.id} type="button" className="ct-chip ct-chip--pick"
                            disabled={locked || !canManage || !target}
                            onClick={() => canManage && seat(person.id)}>
                      {canManage && <Icon name="plus" className="me-1" />}{person.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* ================= 3 — INVITE A COLLEAGUE ================= */}
        {/* ⚠️ Same dead door as the tab before it: the tab is always drawn, so this said nothing
            at all to a host who is not the company admin. Inviting genuinely IS the admin's — it
            adds somebody to the event — so here there is nothing to show read-only, only a
            sentence saying whose job it is. */}
        {!canManage && step === 'invite' && (
          <p className="ce-note">{t('teamsLookOnly')}</p>
        )}
        {canManage && step === 'invite' && (
          <>
            {teams.length === 0 ? (
              <p className="ce-note">
                {t('teamsMakeTeamsFirst')}{' '}
                <button type="button" className="ct-linkbtn" onClick={() => goTo('teams')}>
                  {t('teamsStep_teams')}
                </button>
              </p>
            ) : (
              <>
                <div className="bravos-form">
                  <label>{t('teamsWhichTeam')}</label>
                  <select value={invite.team_id}
                          onChange={(e) => setInvite((v) => ({ ...v, team_id: e.target.value,
                                                               link: '' }))}>
                    <option value="">{t('teamsWhichTeam')}</option>
                    {teams.map((team) => (
                      <option key={team.team_id} value={team.team_id} disabled={full(team)}>
                        {team.name}
                        {cap ? ` (${team.players.length}/${cap})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ---- the colleague, picked ----
                    A search box rather than a scroll: a roster is 56 people here and hundreds at
                    a real customer, and the organiser always already knows the name. */}
                <label className="ct-field mt-2">
                  <span className="ct-pick__label">{t('teamsInviteWho')}</span>
                  <input type="search" value={search} placeholder={t('searchPeople')}
                         onChange={(e) => setSearch(e.target.value)} />
                </label>
                {roster.length === 0 && (
                  <p className="ce-note">
                    {(data.roster || []).length === 0
                      ? t('teamsInviteRosterEmpty') : t('teamsInviteNoMatch')}
                    {' '}
                    <Link to="/company-members">{t('teamsInviteOpenRoster')}</Link>
                  </p>
                )}
                <div className="ct-picklist">
                  {roster.slice(0, 200).map((person) => (
                    <button
                      key={person.id}
                      type="button"
                      className={`ce-person ct-pickrow ${
                        invite.partner_id === person.id ? 'is-on' : ''}`}
                      disabled={busy}
                      onClick={() => setInvite((v) => ({
                        ...v,
                        partner_id: v.partner_id === person.id ? '' : person.id,
                        link: '',
                      }))}
                    >
                      <PersonAvatar person={person} />
                      <span className="ce-person__body">
                        <span className="ce-person__name">{person.name || person.email}</span>
                        <span className="ce-person__email">{person.email}</span>
                      </span>
                      {invite.partner_id === person.id && <Icon name="check" />}
                    </button>
                  ))}
                </div>
                {roster.length > 200 && (
                  <p className="ct-field__hint">{t('teamsInviteTooMany', { n: roster.length })}</p>
                )}

                <p className="ce-check__hint">{t('teamsInviteHint')}</p>
                {/* ★ On an invite-only event the invitation and the door have to agree. The route
                    adds the person to the guest list so the link they follow is not refused —
                    said here, because widening a guest list silently is not something to
                    discover afterwards. */}
                {needsGuestRow && (
                  <p className="ce-note">
                    {t('teamsInviteAddsToGuestList')}{' '}
                    <Link to={`/company-event/${eventId}/people?tab=invites`}>
                      {t('teamsInviteOpenGuestList')}
                    </Link>
                  </p>
                )}
                <div className="ce-actions">
                  <button type="button" className="btn btn-primary"
                          disabled={locked || !!inviteMissing} onClick={sendInvite}>
                    <Icon name="paper-plane" className="me-1" />{t('teamsSendInvite')}
                  </button>
                  <button type="button" className="bravos-member__makeadmin"
                          disabled={locked || !invite.team_id}
                          onClick={() => call('POST', `/team/${invite.team_id}/invite`, {})
                            .then((res) => setInvite((v) => ({ ...v, link: res.invite_link })))
                            .catch(() => {})}>
                    <Icon name="link" className="me-1" />{t('teamsGetLink')}
                  </button>
                </div>
                {/* ★★ What the disabled button is waiting for. It used to be greyed out beside an
                    empty text box, which says nothing at all. */}
                {inviteMissing && <p className="ct-field__hint">{inviteMissing}</p>}
                {invite.link && (
                  <>
                    <input readOnly value={invite.link}
                           onFocus={(e) => e.target.select()} />
                    <p className="ce-check__hint">{t('teamsLinkHint')}</p>
                  </>
                )}
                {/* The settings behind all of this, one click away: whether colleagues may put
                    their own name down at all, and whether the event is by invitation. */}
                <p className="ct-field__hint mt-3">
                  {t('teamsInviteSettingsHint')}{' '}
                  <Link to={`/host-event/${eventId}/settings`}>
                    {t('teamsOpenEventSettings')}
                  </Link>
                </p>
              </>
            )}
          </>
        )}
      </Card>
    </Base>
  );
};

export default CompanyEventTeams;
